import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, Clock, ThumbsUp, ShieldCheck, Star, ArrowLeft } from 'lucide-react';
import { getAds } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';

const FIATS = ['KES','UGX','TZS','RWF','NGN','GHS','ZAR','USD','PKR','INR','PHP','VND','TRY','BRL','MYR','IDR','AED','SAR','EGP','MAD'];
const CRYPTOS = ['USDT','BTC','ETH','BNB','SOL','XRP','DOGE','ADA','AVAX','LTC','DOT','LINK','MATIC','UNI','ATOM'];
const PM_OPTIONS = ['All Payment Methods','M-PESA Kenya (Safaricom)','Bank Transfer','Airtel Money','MTN Mobile Money','Tigo Pesa','Co-operative Bank','Equity Bank','M-PESA Paybill','Cash Deposit to Bank','Access Bank','I&M Bank','Stanbic','Standard Chartered','M-PESA Pochi La Biashara','NCBA Bank','PayPal','AirTM'];

// Country → default fiat mapping
const COUNTRY_FIAT: Record<string, string> = {
  Kenya: 'KES', Uganda: 'UGX', Tanzania: 'TZS', Rwanda: 'RWF',
  Nigeria: 'NGN', Ghana: 'GHS', 'South Africa': 'ZAR',
  Pakistan: 'PKR', India: 'INR', Philippines: 'PHP', Vietnam: 'VND',
  Malaysia: 'MYR', Indonesia: 'IDR', Turkey: 'TRY', Brazil: 'BRL',
  'United Arab Emirates': 'AED', 'Saudi Arabia': 'SAR', Egypt: 'EGP', Morocco: 'MAD',
};

const STORAGE_KEY = 'p2p_selected_fiat';

function getSavedFiat(): string {
  return localStorage.getItem(STORAGE_KEY) || '';
}

function AdCard({ ad, side, fiat, currentUserId }: { ad: any; side: 'buy' | 'sell'; fiat: string; currentUserId?: string }) {
  const navigate = useNavigate();
  const isOwn = !!(currentUserId && (ad.user?._id === currentUserId || ad.user === currentUserId));
  const displayName = ad.user?.nickname || ad.user?.name || 'Unknown';
  const initials = displayName.slice(0, 1).toUpperCase();
  const pm = ad.paymentMethods ?? [];
  const totalRatings = (ad.positiveRatings ?? 0) + (ad.negativeRatings ?? 0);
  const starRating = totalRatings > 0 ? ((ad.positiveRatings ?? 0) / totalRatings * 5).toFixed(1) : null;
  const likeRate = totalRatings > 0 ? Math.round(((ad.positiveRatings ?? 0) / totalRatings) * 100) : null;

  return (
    <div
      className="p2p-ad-card"
      onClick={() => !isOwn && navigate(`/p2p/buy/${ad._id}`)}
      style={{ opacity: isOwn ? 0.6 : 1, cursor: isOwn ? 'default' : 'pointer' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        {/* LEFT COLUMN: avatar+name, price, limits */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* User info block */}
          <div style={{ marginBottom: 4 }}>
            {/* Line 1: avatar + online dot + name + badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {ad.user?.avatar
                  ? <img src={ad.user.avatar} alt={displayName} style={{ objectFit: 'cover', width: 16, height: 16, borderRadius: '50%' }} />
                  : <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'linear-gradient(135deg,#1CD4A7,#3B82F6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#000' }}>{initials}</div>
                }
                {/* Online/offline indicator dot */}
                <span style={{
                  position: 'absolute', bottom: -1, right: -1,
                  width: 6, height: 6, borderRadius: '50%',
                  background: ad.isOnline ? '#1CD4A7' : '#71717A',
                  border: '1px solid #0C0C17',
                }} />
              </div>
              <span className="p2p-ad-name">{displayName}</span>
              {isOwn && <span style={{ fontSize: 10, fontWeight: 700, color: '#F0B90B', background: 'rgba(240,185,11,0.15)', borderRadius: 4, padding: '1px 6px', border: '1px solid rgba(240,185,11,0.3)' }}>Your Ad</span>}
              {!isOwn && ad.user?.kycStatus === 'verified' && (
                <span title="KYC Verified" style={{ display:'flex', alignItems:'center', gap:2, fontSize:10, fontWeight:700, color:'#1CD4A7', background:'rgba(28,212,167,0.12)', borderRadius:4, padding:'1px 5px' }}>
                  <ShieldCheck size={10} />KYC
                </span>
              )}
              {!isOwn && ad.isMerchant && <span style={{ fontSize:10, fontWeight:700, color:'#F0B90B', background:'rgba(240,185,11,0.12)', borderRadius:4, padding:'1px 5px' }}>Merchant</span>}
            </div>
            {/* Line 2: trade stats */}
            <div className="p2p-ad-stats" style={{ display:'flex', alignItems:'center', gap:5, marginTop:2, flexWrap:'wrap' }}>
              <span>Trade: {ad.totalTrades ?? 0} ({ad.completionRate ?? 100}%)</span>
              {likeRate !== null && (<><span style={{ color:'var(--p2p-text-disabled)' }}>·</span><span style={{ display:'flex', alignItems:'center', gap:2, color:'#1CD4A7' }}><ThumbsUp size={10} />{likeRate}%</span></>)}
              {starRating && (<><span style={{ color:'var(--p2p-text-disabled)' }}>·</span><span style={{ display:'flex', alignItems:'center', gap:2, color:'#F0B90B' }}><Star size={10} fill="#F0B90B" />{starRating}</span></>)}
            </div>
          </div>
          {/* Price */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
            <span className="p2p-ad-price">{(ad.currentPrice ?? ad.fixedPrice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
            <span className="p2p-ad-fiat-label">{fiat}/{ad.crypto}</span>
            {ad.priceType === 'floating' && <span className="p2p-badge-fluctuating">~</span>}
          </div>
          {/* Limits */}
          <div className="p2p-ad-limits">Limit: {ad.minOrderAmount?.toLocaleString()} – {ad.maxOrderAmount?.toLocaleString()} {fiat}</div>
          <div className="p2p-ad-limits">Available: {parseFloat((ad.availableAmount ?? 0).toFixed(8)).toString()} {ad.crypto}</div>
        </div>

        {/* RIGHT COLUMN: payment methods + timer + button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0, alignSelf: 'flex-start' }}>
          {pm.slice(0, 4).map((m: any, i: number) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--p2p-text-secondary)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.methodColor ?? '#2563eb', flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.methodType ?? m}{m.extraFields?.bankName ? ` · ${m.extraFields.bankName}` : ''}
              </span>
            </span>
          ))}
          <div className="p2p-ad-timer"><Clock size={12} />{ad.paymentTimeLimit ?? 15} min</div>
          <button
            className={side === 'buy' ? 'p2p-btn-buy' : 'p2p-btn-sell'}
            disabled={isOwn}
            style={{ alignSelf: 'flex-end', flexShrink: 0, ...(isOwn ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
            onClick={e => { e.stopPropagation(); if (!isOwn) navigate(`/p2p/buy/${ad._id}`); }}
          >
            {isOwn ? 'Your Ad' : side === 'buy' ? 'Buy' : 'Sell'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Market() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [crypto, setCrypto] = useState('USDT');
  const [fiat, setFiat] = useState(getSavedFiat() || 'KES');
  const [pm, setPm] = useState('All');
  const [amount, setAmount] = useState('');
  const [ads, setAds] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Auto-detect fiat from KYC country on first visit (only if no saved preference)
  useEffect(() => {
    if (getSavedFiat()) return; // user already chose, don't override
    import('../../lib/authFetch').then(({ authFetch, getAuthHeaders }) => {
      authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/p2p/settings/fees`, { headers: getAuthHeaders() })
        .then((r: any) => r.json())
        .then((d: any) => {
          const country = d?.data?.userCountry;
          if (country && COUNTRY_FIAT[country]) {
            setFiat(COUNTRY_FIAT[country]);
            localStorage.setItem(STORAGE_KEY, COUNTRY_FIAT[country]);
          }
        }).catch(() => {});
    });
  }, []);

  // Persist fiat selection
  const handleFiatChange = (f: string) => {
    setFiat(f);
    localStorage.setItem(STORAGE_KEY, f);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { side, crypto, fiat };
      if (amount) params.amount = amount;
      if (pm !== 'All') params.paymentMethod = pm;
      const res = await getAds(params);
      setAds(res.ads ?? []);
    } catch { setAds([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [side, crypto, fiat, pm]);

  return (
    <div className="p2p-page">
      {/* Header — with back button integrated */}
      <div className="p2p-header">
        <button onClick={() => navigate('/home')} style={{ background: 'none', border: 'none', color: 'var(--p2p-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px 8px 4px 0' }}>
          <ArrowLeft size={20} />
        </button>
        <span className="p2p-header-title">P2P</span>
        <select className="p2p-filter-select" value={fiat} onChange={e => handleFiatChange(e.target.value)} style={{ border: 'none', background: 'transparent', color: 'var(--p2p-text-secondary)', fontSize: 14, cursor: 'pointer' }}>
          {FIATS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      <div className="p2p-page-content">
      {/* Buy/Sell Toggle */}
      <div style={{ padding: '12px 16px' }}>
        <div className="p2p-trade-toggle">
          <button className={side === 'buy' ? 'buy-active' : ''} onClick={() => setSide('buy')}>Buy</button>
          <button className={side === 'sell' ? 'sell-active' : ''} onClick={() => setSide('sell')}>Sell</button>
        </div>
      </div>

      {/* Filters — all dropdowns */}
      <div style={{ padding: '0 16px 12px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* Crypto dropdown */}
        <select
          value={crypto}
          onChange={e => { setCrypto(e.target.value); }}
          className="p2p-filter-select"
          style={{ flex: '1 1 100px', minWidth: 90 }}
        >
          {CRYPTOS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Amount input */}
        <div style={{ flex: '2 1 130px', position: 'relative', minWidth: 120 }}>
          <input
            className="p2p-filter-select"
            placeholder="Enter amount"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            onBlur={load}
            type="number"
            style={{ width: '100%', paddingRight: 40 }}
          />
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--p2p-text-muted)', fontSize: 12, pointerEvents: 'none' }}>{fiat}</span>
        </div>

        {/* Payment method dropdown */}
        <select
          value={pm}
          onChange={e => setPm(e.target.value)}
          className="p2p-filter-select"
          style={{ flex: '2 1 150px', minWidth: 140 }}
        >
          {PM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Ad List */}
      {loading ? (
        <div>{[...Array(5)].map((_, i) => (
          <div key={i} className="p2p-skel-card">
            <div className="p2p-skel-left">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="p2p-skel" style={{ width: 16, height: 16, borderRadius: '50%' }} />
                <span className="p2p-skel" style={{ width: 100, height: 14 }} />
                <span className="p2p-skel" style={{ width: 30, height: 14 }} />
              </div>
              <span className="p2p-skel" style={{ width: 60, height: 10 }} />
              <span className="p2p-skel" style={{ width: 120, height: 22 }} />
              <span className="p2p-skel" style={{ width: 140, height: 10 }} />
              <span className="p2p-skel" style={{ width: 130, height: 10 }} />
            </div>
            <div className="p2p-skel-right">
              <span className="p2p-skel" style={{ width: 110, height: 11 }} />
              <span className="p2p-skel" style={{ width: 90, height: 11 }} />
              <span className="p2p-skel" style={{ width: 60, height: 10 }} />
              <span className="p2p-skel" style={{ width: 60, height: 28, borderRadius: 6 }} />
            </div>
          </div>
        ))}</div>
      ) : ads.length === 0 ? (
        <div className="p2p-empty">
          <SlidersHorizontal size={40} className="p2p-empty-icon" />
          <p className="p2p-empty-text">No ads found. Try adjusting filters.</p>
        </div>
      ) : (
        <div>{ads.filter(ad => !(user?._id && (ad.user?._id === user._id || ad.user === user._id))).map(ad => <AdCard key={ad._id} ad={ad} side={side} fiat={fiat} currentUserId={user?._id} />)}</div>
      )}
      </div>
    </div>
  );
}
